
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BasicInfoTab from "./BasicInfoTab";
import MediaTab from "./MediaTab";
import InventoryTab from "./InventoryTab";
import ProductSEOManager from "./ProductSEOManager";
import ContentTab from "./ContentTab";
import ProductVariantsManager from "./ProductVariantsManager";
import { validateProductData, sanitizeProductData, type ProductFormData } from "@/utils/productHelpers";

// Local variant type for form management (without database-specific fields)
interface LocalProductVariant {
  id?: string;
  name: string;
  sku?: string;
  price?: number;
  compare_at_price?: number;
  stock_quantity: number;
  weight?: number;
  variant_options?: Record<string, string>;
  image_url?: string;
  is_active: boolean;
}

interface EnhancedProductFormProps {
  product?: any;
  categories: any[];
  onSaved: () => void;
  onCancel: () => void;
}

const EnhancedProductForm = ({ product, categories, onSaved, onCancel }: EnhancedProductFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[]>(product?.gallery_images || []);
  const [variants, setVariants] = useState<LocalProductVariant[]>([]);
  const [contentSections, setContentSections] = useState<any[]>([]);

  // Fetch existing variants for existing products
  const { data: existingVariants } = useQuery({
    queryKey: ['product-variants', product?.id],
    queryFn: async () => {
      if (!product?.id) return [];
      const { data, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', product.id)
        .order('display_order');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!product?.id
  });

  useEffect(() => {
    if (existingVariants) {
      // Convert database variants to our local variant type
      const convertedVariants: LocalProductVariant[] = existingVariants.map(variant => ({
        id: variant.id,
        name: variant.name,
        sku: variant.sku || '',
        price: variant.price || 0,
        compare_at_price: variant.compare_at_price,
        stock_quantity: variant.stock_quantity,
        weight: variant.weight,
        variant_options: typeof variant.variant_options === 'object' ? variant.variant_options as Record<string, string> : {},
        image_url: variant.image_url,
        is_active: variant.is_active
      }));
      setVariants(convertedVariants);
    }
  }, [existingVariants]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      name: product?.name || "",
      description: product?.description || "",
      price: product?.price || 0,
      compare_at_price: product?.compare_at_price || undefined,
      category_id: product?.category_id || "none",
      stock_quantity: product?.stock_quantity || 0,
      sku: product?.sku || "",
      status: product?.status || "active",
      featured: product?.featured || false,
      weight: product?.weight || undefined,
      image_url: product?.image_url || "",
      tags: product?.tags?.join(", ") || "",
      seo_title: product?.seo_title || "",
      seo_description: product?.seo_description || "",
      seo_keywords: product?.seo_keywords || "",
      url_slug: product?.url_slug || "",
      seo_enabled: product?.seo_enabled || false,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      // Validate the form data
      const validation = validateProductData(data);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Sanitize and prepare the data
      const productData = sanitizeProductData(data, galleryImages);
      
      let productId = product?.id;

      try {
        if (product) {
          // Update existing product
          const { data: updatedProduct, error } = await supabase
            .from('products')
            .update(productData)
            .eq('id', product.id)
            .select('id')
            .single();
            
          if (error) {
            throw new Error(`Failed to update product: ${error.message}`);
          }
        } else {
          // Create new product
          const { data: newProduct, error } = await supabase
            .from('products')
            .insert([productData])
            .select('id')
            .single();
            
          if (error) {
            throw new Error(`Failed to create product: ${error.message}`);
          }
          
          productId = newProduct.id;
        }

        // Save variants if any exist
        if (variants.length > 0) {
          // Delete existing variants
          await supabase
            .from('product_variants')
            .delete()
            .eq('product_id', productId);

          // Insert new variants
          const variantsToInsert = variants.map((variant, index) => ({
            product_id: productId,
            name: variant.name,
            sku: variant.sku || null,
            price: variant.price || 0,
            compare_at_price: variant.compare_at_price || null,
            stock_quantity: variant.stock_quantity,
            weight: variant.weight || null,
            variant_options: variant.variant_options || {},
            image_url: variant.image_url || null,
            is_active: variant.is_active,
            display_order: index
          }));

          const { error: variantsError } = await supabase
            .from('product_variants')
            .insert(variantsToInsert);

          if (variantsError) {
            throw new Error(`Failed to save variants: ${variantsError.message}`);
          }
        }

        // Save content sections if any exist
        if (contentSections.length > 0) {
          // Delete existing content sections
          await supabase
            .from('product_content_sections')
            .delete()
            .eq('product_id', productId);

          // Insert new content sections
          const sectionsToInsert = contentSections.map((section, index) => ({
            product_id: productId,
            section_type: section.section_type,
            title: section.title,
            subtitle: section.subtitle || null,
            content: section.content || {},
            layout_config: section.layout_config || {},
            display_order: index,
            is_active: true
          }));

          const { error: sectionsError } = await supabase
            .from('product_content_sections')
            .insert(sectionsToInsert);

          if (sectionsError) {
            throw new Error(`Failed to save content sections: ${sectionsError.message}`);
          }
        }

        return productId;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (productId) => {
      toast({
        title: "Success",
        description: `Product ${product ? 'updated' : 'created'} successfully`,
      });
      onSaved();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || `Failed to ${product ? 'update' : 'create'} product`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: ProductFormData) => {
    setIsSubmitting(true);
    
    try {
      await mutation.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="variants">Variants</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <BasicInfoTab
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            categories={categories}
          />
        </TabsContent>

        <TabsContent value="media" className="space-y-6">
          <MediaTab
            register={register}
            watch={watch}
            setValue={setValue}
            galleryImages={galleryImages}
            onGalleryImagesChange={setGalleryImages}
          />
        </TabsContent>

        <TabsContent value="inventory" className="space-y-6">
          <InventoryTab
            register={register}
            watch={watch}
            product={product}
          />
        </TabsContent>

        <TabsContent value="variants" className="space-y-6">
          <ProductVariantsManager
            variants={variants as any}
            onVariantsChange={(newVariants: any) => setVariants(newVariants)}
          />
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <ContentTab
            productId={product?.id || ''}
            contentSections={contentSections}
            onContentSectionsChange={setContentSections}
          />
        </TabsContent>

        <TabsContent value="seo" className="space-y-6">
          <ProductSEOManager
            register={register}
            watch={watch}
            setValue={setValue}
          />
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-admin-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-admin-accent-blue hover:bg-admin-accent-blue/90"
        >
          {isSubmitting ? "Saving..." : product ? "Update Product" : "Create Product"}
        </Button>
      </div>
    </form>
  );
};

export default EnhancedProductForm;
